'use client';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { createClient } from '@/lib/supabase/client';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { LoadingSpinner } from '../loading-spinner';
import BottomGradient from '../ui/bottom-gradient';
import { Button } from '../ui/button';
import { GlowingEffect } from '../ui/glowing-effect';
import { Separator } from '../ui/separator';
import GoogleLogin from './google-login';

type FormData = z.infer<typeof formSchema>;

const formSchema = z.object({
  email: z.string().nonempty('Email is required').email('Invalid email address'),
  password: z
    .string()
    .min(6, 'Password must be at least 6 characters')
    .regex(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
      'Password must include letters, numbers & special characters',
    ),
});

function LoginForm() {
  const supabase = createClient();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const callbackUrl = searchParams.get('callbackUrl');

  const onSubmit = async (formData: FormData) => {
    setLoading(true);
    setError(null);
    const { email, password } = formData;
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      reset();
      if (callbackUrl) {
        router.push(callbackUrl);
      } else {
        router.push('/');
      }
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='shadow-input relative mx-auto my-24 w-full max-w-md rounded-2xl bg-white p-4 transition-all duration-500 md:p-8 dark:bg-slate-950'>
      <GlowingEffect
        blur={0}
        borderWidth={3}
        spread={30}
        glow={true}
        disabled={false}
        proximity={64}
        inactiveZone={0.01}
      />
      <h2 className='text-xl font-bold text-neutral-800 dark:text-neutral-200'>Welcome Back</h2>
      <p className='my-2 max-w-sm text-sm text-neutral-600 dark:text-neutral-300'>
        Enter your credentials to access your account
      </p>

      <form className='space-y-6' onSubmit={handleSubmit(onSubmit)}>
        <LabelInputContainer className='mb-4'>
          <Label htmlFor='email'>Email Address</Label>
          <Input
            id='email'
            {...register('email')}
            autoComplete='off'
            placeholder='hello@example.com'
          />
          {errors.email && <span className='text-sm text-red-500'>{errors.email.message}</span>}
        </LabelInputContainer>
        <LabelInputContainer className='relative'>
          <Label htmlFor='password'>Password</Label>
          <div className='relative'>
            <Input
              id='password'
              type={showPassword ? 'text' : 'password'}
              {...register('password')}
              autoComplete='new-password'
              placeholder='••••••••'
            />
            <Button
              variant='ghost'
              type='button'
              className='text-muted-foreground absolute top-1/2 right-3 size-6 -translate-y-1/2 transform'
              onClick={() => setShowPassword((prev) => !prev)}
              tabIndex={-1}
              size='icon'
            >
              {showPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
            </Button>
          </div>
          {errors.password && (
            <span className='text-sm text-red-500'>{errors.password.message}</span>
          )}
        </LabelInputContainer>

        <button
          className='group/btn relative block h-10 w-full cursor-pointer rounded-md bg-linear-to-br from-black to-neutral-600 font-medium text-white disabled:opacity-60 dark:bg-zinc-800 dark:from-zinc-950 dark:to-zinc-900'
          type='submit'
          disabled={loading}
        >
          {loading ? 'Logging in...' : 'Login'}
          <BottomGradient />
        </button>
        <div className='text-right text-sm'>
          <Link href='/forgot-password' className='text-blue-600 hover:underline'>
            Forgot Password?
          </Link>
        </div>
        {error && (
          <div className='bg-destructive/10 text-destructive my-2 rounded-md p-3 text-sm'>
            {error}
          </div>
        )}
        <div className='relative'>
          <div className='absolute inset-0 flex items-center'>
            <Separator className='w-full' />
          </div>
          <div className='relative flex justify-center text-xs uppercase'>
            <span className='bg-background text-muted-foreground px-2'>Or continue with</span>
          </div>
        </div>

        <GoogleLogin />
        <div className='mt-5 text-center text-sm'>
          Don&apos;t have an account?{' '}
          <Link
            href={
              callbackUrl ? `/signup?callbackUrl=${encodeURIComponent(callbackUrl)}` : '/signup'
            }
            className='text-blue-700 hover:text-blue-800 hover:underline'
          >
            Sign up
          </Link>
        </div>
      </form>
    </div>
  );
}

const LabelInputContainer = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={cn('flex w-full flex-col space-y-2', className)}>{children}</div>;

export default function LoginFormPage() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <LoginForm />
    </Suspense>
  );
}
